import AmnisLoader from 'components/ui/AmnisLoader/AmnisLoader';
import classNames from 'classnames';
import {formatDateInZurichTZ} from 'helpers/dates';
import {
    formatIban,
    formatNumber,
    removeThousandSeparator,
} from 'helpers/common';
import StatusCell from 'components/ui/Table/components/StatusCell/StatusCell';
import Button from 'components/ui/Button';
import IconSVG from 'components/ui/Icon/IconSVG';
import Collapse from 'components/ui/Collapse/Collapse';
import {WALLET_IBAN_MAPPING} from 'components/modals/AccountDetailsModal/useAccountDetailsModal';
import Alert from 'components/ui/Alert/Alert';
import Illustration from 'components/pages/PaymentRequestPage/components/illustrations/dynamic-discount.svg';
import Badge from 'components/ui/Badge/Badge';
import {PATH} from 'constants/common';
import {ROUTING_TYPE_MAPPING} from 'constants/wallet';
import {createFaqLink} from 'helpers/url';
import PublicPageFooter from 'components/ui/PublicPageFooter/PublicPageFooter';
import PaymentRequestDynamicDiscountChart from 'components/pages/PaymentRequestPage/components/PaymentRequestDynamicDiscountChart/PaymentRequestDynamicDiscountChart';
import AccordionItem, {Accordion} from 'components/ui/Accordion/Accordion';
import PublicHeader from 'components/layout/Header/PublicHeader';
import usePaymentRequestPublicPage from './usePaymentRequestPublicPage';
import styles from './PaymentRequestPublic.module.scss';

const PaymentRequestPublicPage = () => {
    const {
        data,
        isLoading,
        user,
        downloadDocument,
        isDownloadLoading,
        handlePayMethodChange,
        payMethod,
        activeBusinessPartner,
        handleConnectPeer,
        isConnectPeerLoading,
        handleCreatePeerPayment,
        error,
        handleDissmissNotFound,
        history,
        loadingDocuments,
        selectedLanguageParams,
        countries,
    } = usePaymentRequestPublicPage();

    if (isLoading) {
        return <AmnisLoader />;
    }

    const content = () => {
        if (!data) {
            return (
                <>
                    <h1>Payment request was not found.</h1>
                    <div className={styles.paymentRequestData}>
                        <div
                            className={classNames(
                                styles.dataRow,
                                styles.details,
                            )}
                        >
                            We couldn&apos;t find the payment request you were
                            looking for. Please get in touch with your business
                            partner!
                        </div>
                        <div
                            className={classNames(
                                styles.paymentMethods,
                                styles.dataRow,
                            )}
                        >
                            <Button onClick={handleDissmissNotFound}>
                                Back to home
                            </Button>
                        </div>
                    </div>
                </>
            );
        }

        return (
            <>
                <h1>
                    Payment request&nbsp;
                    <var data-var="request-id">
                        -&nbsp;
                        {data.invoiceNumber ??
                            `#${data['@id'].split('/').pop()}`}
                    </var>
                </h1>
                <div className={styles.paymentRequestData}>
                    <div
                        className={classNames(
                            styles.header,
                            styles.dataRow,
                            styles.addressLine,
                        )}
                        data-notranslate
                    >
                        <p className={styles.companyName}>
                            {data.businessPartner.companyName}
                        </p>
                        <p>{data.businessPartner.address}</p>
                        <p>
                            {data.businessPartner.zip}&nbsp;
                            {data.businessPartner.city}
                        </p>
                        <p>
                            {data.businessPartner
                                .countryOfIncorporation!.split('/')
                                .pop()}
                        </p>
                    </div>
                    <div
                        className={classNames(styles.recipient, styles.dataRow)}
                    >
                        <p className={styles.label}>Recipient</p>
                        <p className={styles.recipientName} data-notranslate>
                            {data.contact.name}
                        </p>
                        <div className={styles.addressLine} data-notranslate>
                            <p>{data.contact.address}</p>
                            <p>
                                {data.contact.zip}&nbsp;{data.contact.city}
                            </p>
                            <p>{data.contact.country.split('/').pop()}</p>
                        </div>
                    </div>
                    <div className={classNames(styles.details, styles.dataRow)}>
                        <div>
                            <p className={styles.label}>Attachments</p>
                            <p>
                                <var data-var="file-count">
                                    {data.documents.length}
                                </var>
                                &nbsp;file(s)
                            </p>
                            <div className={styles.files}>
                                {data.documents.map(document => (
                                    <Button
                                        icon={<IconSVG name="download_old" />}
                                        color="secondary"
                                        key={document['@id']}
                                        isLoading={
                                            isDownloadLoading &&
                                            loadingDocuments.includes(
                                                document['@id'],
                                            )
                                        }
                                        onClick={() =>
                                            downloadDocument(document['@id'])
                                        }
                                    >
                                        {document.originalFileName}
                                    </Button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className={styles.label}>Recipient</p>
                            <p data-notranslate>{data.email}</p>
                        </div>
                    </div>
                    <div className={classNames(styles.details, styles.dataRow)}>
                        <div>
                            <p className={styles.label}>Invoice date</p>
                            <p data-notranslate>
                                {formatDateInZurichTZ(data.scheduleDate)}
                            </p>
                        </div>
                        <div>
                            <p className={styles.label}>Due date</p>
                            <p className={styles.dueDate}>
                                <span data-notranslate className={styles.date}>
                                    {formatDateInZurichTZ(data.dueDate)}
                                </span>
                                &nbsp;
                                <StatusCell
                                    type="paymentRequest"
                                    dueDate={data.dueDate}
                                    pending={data.pending}
                                    draft={data.draft}
                                />
                            </p>
                        </div>
                        <div>
                            <p className={styles.label}>Total amount due</p>
                            <p data-notranslate className={styles.amount}>
                                {formatNumber(data.amount)}&nbsp;
                                {data.currency.split('/').pop()}
                            </p>
                        </div>
                    </div>
                    {data.discountDays && data.discountPercent ? (
                        <div className={classNames(styles.discount)}>
                            <div className={styles.illustration}>
                                <Illustration />
                            </div>
                            <div className={styles.textContainer}>
                                <p className={styles.title}>Good news!</p>
                                <p>
                                    <var data-var="company-name">
                                        {data.businessPartner.companyName}
                                    </var>
                                    &nbsp;offers you a dynamic discount: The
                                    earlier you pay via&nbsp;
                                    <Badge content="PEER" color="primary" />,
                                    the greater your discount!
                                </p>
                                <div className={styles.discountValues}>
                                    <p>
                                        Your dynamic discount today:&nbsp;
                                        <span
                                            className={styles.discountPercent}
                                        >
                                            <var data-var="discount-percent">
                                                {formatNumber(
                                                    data.todayDiscountPercent,
                                                )}
                                                %
                                            </var>
                                        </span>
                                        <div>
                                            <Button
                                                variant="text"
                                                onClick={() =>
                                                    window.open(
                                                        createFaqLink(
                                                            selectedLanguageParams,
                                                            '/articles/6889561-what-is-dynamic-discounting',
                                                        ),
                                                    )
                                                }
                                            >
                                                Learn more
                                            </Button>
                                        </div>
                                    </p>
                                    <div data-notranslate>
                                        <div className={styles.discountAmount}>
                                            -
                                            {formatNumber(
                                                data.amount - data.todayAmount,
                                            )}
                                            &nbsp;
                                            {data.currency.split('/').pop()}
                                        </div>
                                        <div className={styles.todayAmount}>
                                            {formatNumber(data.todayAmount)}
                                            &nbsp;
                                            {data.currency.split('/').pop()}
                                        </div>
                                    </div>
                                    <div>
                                        <Accordion toggleMode="single">
                                            <AccordionItem
                                                accordionKey="chart"
                                                toggleType="title"
                                                titleClass={
                                                    styles.accordionToggle
                                                }
                                                title={
                                                    <Button
                                                        variant="text"
                                                        icon="line-chart"
                                                    >
                                                        See your dynamic
                                                        discount as chart
                                                    </Button>
                                                }
                                                body={
                                                    <PaymentRequestDynamicDiscountChart
                                                        amount={removeThousandSeparator(
                                                            data?.amount,
                                                        )}
                                                        discountDays={
                                                            data?.discountDays
                                                        }
                                                        discountPercent={
                                                            data?.discountPercent
                                                        }
                                                        invoiceDate={
                                                            new Date(
                                                                data?.scheduleDate,
                                                            )
                                                        }
                                                    />
                                                }
                                            />
                                        </Accordion>
                                    </div>
                                </div>
                                {!activeBusinessPartner ? (
                                    <>
                                        <p>
                                            Get your amnis account and
                                            activate&nbsp;
                                            <Badge
                                                content="PEER"
                                                color="primary"
                                            />{' '}
                                            to benefit from your discount and
                                            maximize your savings:
                                        </p>
                                        <Button
                                            onClick={() =>
                                                history.push(PATH.REGISTRATION)
                                            }
                                        >
                                            Sign up for free
                                        </Button>
                                    </>
                                ) : null}
                            </div>
                            <div className={styles.arrow} />
                        </div>
                    ) : null}
                    <div
                        className={classNames(
                            styles.paymentMethods,
                            styles.dataRow,
                        )}
                    >
                        <p>
                            We offer you the following payment methods via amnis
                            so that you can execute your payment fast and
                            easily:
                        </p>
                        <div className={styles.buttonRow}>
                            <Button
                                icon={<IconSVG name="amnis" />}
                                onClick={() => handlePayMethodChange('peer')}
                                className={styles.peerButton}
                            >
                                {data.discountDays && data.discountPercent ? (
                                    <div
                                        className={styles.badge}
                                        data-notranslate
                                    >
                                        -{data.todayDiscountPercent}%
                                    </div>
                                ) : null}
                                Pay via PEER
                            </Button>
                            <Button
                                icon={<IconSVG name="fee" />}
                                onClick={() => handlePayMethodChange('iban')}
                                disabled={data.virtualIbans === null}
                            >
                                Pay via IBAN
                            </Button>
                            <Button
                                className={styles.cardButton}
                                icon={<IconSVG name="card" />}
                                onClick={() =>
                                    window.open(
                                        'https://ideas.amnistreasury.com/',
                                        '_blank',
                                    )
                                }
                                disabled
                            >
                                Pay via Credit Card
                                <div className={styles.badge}>COMING SOON</div>
                            </Button>
                        </div>
                    </div>
                    <Collapse on={payMethod === 'peer'}>
                        <div className={classNames(styles.dataRow)}>
                            <div className={styles.paymentText}>
                                <p>
                                    <Button
                                        variant="text"
                                        onClick={() =>
                                            window.open(
                                                'https://amnistreasury.com/peer-to-peer-payment/',
                                                '_blank',
                                            )
                                        }
                                    >
                                        PEER
                                    </Button>{' '}
                                    allows you to transfer money instantly and
                                    free of charge via our amnis Peer-to-Peer
                                    payment network!
                                </p>
                                {!!activeBusinessPartner &&
                                !data.senderContact
                                    ?.connectedBusinessPartner ? (
                                    <>
                                        {activeBusinessPartner['@id'] ===
                                        data.businessPartner['@id'] ? (
                                            <Alert
                                                message="Peer payments can only be enabled to other parties than yourself."
                                                variant="warning"
                                            />
                                        ) : (
                                            <Button
                                                onClick={handleConnectPeer}
                                                isLoading={isConnectPeerLoading}
                                            >
                                                Enable peer payment with&nbsp;
                                                <var data-var="company-name">
                                                    {
                                                        data.businessPartner
                                                            .companyName
                                                    }
                                                </var>
                                            </Button>
                                        )}
                                    </>
                                ) : null}
                                {activeBusinessPartner &&
                                data.senderContact?.connectedBusinessPartner ? (
                                    <Button onClick={handleCreatePeerPayment}>
                                        Create PEER payment
                                    </Button>
                                ) : null}
                                {!activeBusinessPartner ? (
                                    <>
                                        <p>
                                            If you don&apos;t have an amnis
                                            account yet, you can sign up for
                                            free and get your amnis account
                                            within 48 hours, thanks to our
                                            completely digital onboarding
                                            process. Once you have successfully
                                            onboarded, you can join &nbsp;
                                            <var data-var="business-partner">
                                                {
                                                    data.businessPartner
                                                        .companyName
                                                }
                                            </var>
                                            &apos;s PEER network and immediately
                                            settle invoices instantly and free
                                            of charge - 24/7.
                                        </p>
                                        <div className={styles.peerCta}>
                                            <Button
                                                onClick={() =>
                                                    history.push(
                                                        PATH.REGISTRATION,
                                                    )
                                                }
                                            >
                                                Sign up for free
                                            </Button>
                                            <Button
                                                variant="text"
                                                onClick={() =>
                                                    history.push(PATH.LOGIN)
                                                }
                                            >
                                                Do you already have an amnis
                                                account?
                                            </Button>
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </Collapse>
                    <Collapse on={payMethod === 'iban'}>
                        <div className={styles.dataRow}>
                            <p className={styles.paymentText}>
                                You can settle the invoice by wiring the
                                outstanding amount to the following account(s):
                            </p>
                        </div>
                        {data.virtualIbans
                            ?.sort((a, b) =>
                                a.paymentType === 'regular' ? -1 : 1,
                            )
                            .map(iban => (
                                <div key={iban.iban} className={styles.dataRow}>
                                    <h3>
                                        {iban.paymentType === 'priority'
                                            ? 'SWIFT Payment'
                                            : (
                                                  WALLET_IBAN_MAPPING[
                                                      iban.routingType
                                                  ] ??
                                                  WALLET_IBAN_MAPPING[
                                                      data.currency
                                                          .split('/')
                                                          .pop()!
                                                  ]
                                              )?.title}
                                    </h3>
                                    <div className={styles.ibanRecipient}>
                                        <p className={styles.label}>
                                            Recipient
                                        </p>
                                        <p
                                            className={styles.recipientName}
                                            data-notranslate
                                        >
                                            {data.businessPartner.companyName}
                                        </p>
                                        <div
                                            className={styles.addressLine}
                                            data-notranslate
                                        >
                                            <p>
                                                {data.businessPartner.address}
                                            </p>
                                            <p>
                                                {data.businessPartner.zip}&nbsp;
                                                {data.businessPartner.city}
                                            </p>
                                            <p>
                                                {data.businessPartner
                                                    .countryOfIncorporation!.split(
                                                        '/',
                                                    )
                                                    .pop()}
                                            </p>
                                        </div>
                                    </div>
                                    <div className={styles.bankDetails}>
                                        <div>
                                            <p className={styles.label}>
                                                IBAN/Account
                                            </p>
                                            <p>{formatIban(iban.iban)}</p>
                                        </div>
                                        <div>
                                            {iban.routingType &&
                                            iban.routingType !== 'bic' &&
                                            iban.routingCode ? (
                                                <>
                                                    <p className={styles.label}>
                                                        {
                                                            ROUTING_TYPE_MAPPING[
                                                                iban.routingType
                                                            ]
                                                        }
                                                    </p>
                                                    <p>{iban.routingCode}</p>
                                                </>
                                            ) : null}
                                        </div>
                                        <div>
                                            <p className={styles.label}>Bank</p>
                                            <p>
                                                <span>{iban.bankName}</span>
                                                <br />
                                                <span>
                                                    {iban.address.replace(
                                                        ', The Steward Building',
                                                        '',
                                                    )}
                                                </span>
                                                <br />
                                                <span>
                                                    {
                                                        countries?.find(
                                                            country =>
                                                                country[
                                                                    '@id'
                                                                ] ===
                                                                iban.country,
                                                        )?.name
                                                    }
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </Collapse>
                    {error ? (
                        <div className={styles.error}>
                            <Alert message={error} variant="danger" />
                        </div>
                    ) : null}

                    {!user ? (
                        <div
                            className={classNames(
                                styles.footer,
                                styles.dataRow,
                            )}
                        >
                            <p>Would you like to learn more about amnis?</p>
                            <p>
                                amnis offers an all-in-one business account to
                                transfer money abroad, exchange currencies and
                                collect international payments - fast and
                                conveniently. So you don&apos;t need multiple
                                bank accounts anymore to manage your foreign
                                currencies!
                            </p>
                            <Button
                                variant="text"
                                onClick={() =>
                                    window.open(
                                        'https://amnistreasury.com',
                                        '_blank',
                                    )
                                }
                            >
                                Learn more
                            </Button>
                        </div>
                    ) : null}
                </div>
            </>
        );
    };

    return (
        <div className={styles.container}>
            <PublicHeader />
            <div className={styles.content}>{content()}</div>
            <PublicPageFooter />
        </div>
    );
};

export default PaymentRequestPublicPage;
